import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Building2,
  User,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  Clock,
  Globe,
  DollarSign,
  Loader2,
  Calculator,
  Sparkles,
  Target,
  AlertTriangle,
  Lightbulb,
  Award,
  Search,
  Zap,
  Palette,
  Users,
  ShoppingCart,
  Package,
  CalendarClock,
  UserCog,
  Shield,
  Star,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  { value: "saas", label: "SaaS" },
  { value: "ecommerce", label: "Ecommerce" },
  { value: "content", label: "Content Website" },
  { value: "marketplace", label: "Marketplace" },
  { value: "app", label: "App" },
  { value: "agency", label: "Agency" },
];

const TRAFFIC_DIVERSITY = [
  { value: "single", label: "Single Source" },
  { value: "two_sources", label: "Two Sources" },
  { value: "diversified", label: "Diversified (3+)" },
];

const REVENUE_TYPES = [
  { value: "advertising", label: "Advertising" },
  { value: "affiliate", label: "Affiliate" },
  { value: "product_sales", label: "Product Sales" },
  { value: "recurring_subscription", label: "Recurring Subscription" },
  { value: "mixed", label: "Mixed" },
];

const SELL_TIMELINE = [
  { value: "asap", label: "ASAP" },
  { value: "twelve_months", label: "Within the Next 12 Months" },
  { value: "one_year", label: "More Than a Year" },
  { value: "five_years", label: "In the Next 5 Years" },
  { value: "not_selling", label: "Not at All" },
];

const SALE_PREFERENCE = [
  { value: "self_managed", label: "I want to control my own sale and invest time in the process" },
  { value: "broker_managed", label: "I'd prefer someone to manage the process for me" },
];

const STEPS = [
  { id: 1, title: "Website Analysis", icon: Search },
  { id: 2, title: "Your Details", icon: User },
  { id: 3, title: "Business Info", icon: Building2 },
  { id: 4, title: "Traffic Data", icon: BarChart3 },
  { id: 5, title: "Results", icon: CheckCircle2 },
];

const ValuationWidget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  const [websiteAnalysis, setWebsiteAnalysis] = useState(null);

  // Website URL for analysis
  const [websiteUrl, setWebsiteUrl] = useState("");

  // Lead data
  const [leadData, setLeadData] = useState({
    name: "",
    email: "",
    phone: "",
    website_url: "",
    sell_timeline: "",
    sale_preference: "",
  });

  // Business data
  const [businessData, setBusinessData] = useState({
    business_type: "",
    years_operating: "",
    owner_hours_per_week: "",
    monthly_revenue: "",
    monthly_profit: "",
    revenue_growth_rate: "",
    traffic_diversity: "",
    revenue_type: "",
    recurring_revenue_percentage: "",
    churn_rate: "",
    average_stock_level: "",
  });

  // Integration data
  const [integrationData, setIntegrationData] = useState({
    monthly_visitors: "",
    monthly_page_views: "",
  });

  // Track widget load
  useEffect(() => {
    axios.post(`${API_URL}/analytics/track-load`).catch(console.error);
    
    // If landing on /ValuationEcom with state data (redirected after submission)
    if (location.pathname === '/ValuationEcom' && location.state?.valuationResult) {
      setValuationResult(location.state.valuationResult);
      setCurrentStep(5);
    }
  }, [location]);

  const progress = (currentStep / 5) * 100;

  const handleLeadChange = (field, value) => {
    setLeadData({ ...leadData, [field]: value });
  };

  const handleBusinessChange = (field, value) => {
    setBusinessData({ ...businessData, [field]: value });
  };

  const handleIntegrationChange = (e) => {
    setIntegrationData({ ...integrationData, [e.target.name]: e.target.value });
  };

  const handleWebsiteAnalysis = async () => {
    if (!websiteUrl) {
      toast.error("Please enter a website URL");
      return;
    }

    // Basic URL validation
    let urlToAnalyze = websiteUrl;
    if (!urlToAnalyze.startsWith("http://") && !urlToAnalyze.startsWith("https://")) {
      urlToAnalyze = "https://" + urlToAnalyze;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/analyze-website`, {
        url: urlToAnalyze,
      }, { timeout: 45000 });
      setWebsiteAnalysis(response.data);
      setLeadData({ ...leadData, website_url: urlToAnalyze });
      
      // Pre-fill business type if detected
      if (response.data.business_type) {
        const detectedType = response.data.business_type.toLowerCase();
        const matchedType = BUSINESS_TYPES.find(t => 
          detectedType.includes(t.value) || t.label.toLowerCase().includes(detectedType)
        );
        if (matchedType) {
          setBusinessData(prev => ({ ...prev, business_type: matchedType.value }));
        }
      }
      
      toast.success("Website analysis complete!");
      // Don't auto-advance - let user see results first
    } catch (error) {
      console.error("Analysis error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Analysis took too long. Please try again or skip this step.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to analyze website. Please check the URL and try again.");
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const validateStep2 = () => {
    if (!leadData.name || !leadData.email || !leadData.phone) {
      toast.error("Please fill in all required contact fields");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!leadData.sell_timeline) {
      toast.error("Please select when you're looking to sell");
      return false;
    }
    if (!leadData.sale_preference) {
      toast.error("Please select your sale preference");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (
      !businessData.business_type ||
      !businessData.years_operating ||
      !businessData.owner_hours_per_week ||
      !businessData.revenue_growth_rate ||
      !businessData.traffic_diversity ||
      !businessData.revenue_type
    ) {
      toast.error("Please fill in all required fields");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const payload = {
        lead: {
          name: leadData.name,
          email: leadData.email,
          phone: leadData.phone,
          website_url: leadData.website_url,
          sell_timeline: leadData.sell_timeline,
          sale_preference: leadData.sale_preference,
        },
        business: {
          business_type: businessData.business_type,
          years_operating: parseFloat(businessData.years_operating) || 0,
          owner_hours_per_week: parseFloat(businessData.owner_hours_per_week) || 0,
          monthly_revenue: businessData.monthly_revenue ? parseFloat(businessData.monthly_revenue) : null,
          monthly_profit: businessData.monthly_profit ? parseFloat(businessData.monthly_profit) : null,
          revenue_growth_rate: parseFloat(businessData.revenue_growth_rate) || 0,
          traffic_diversity: businessData.traffic_diversity,
          revenue_type: businessData.revenue_type,
          recurring_revenue_percentage: businessData.recurring_revenue_percentage ? parseFloat(businessData.recurring_revenue_percentage) : 0,
          churn_rate: businessData.churn_rate ? parseFloat(businessData.churn_rate) : 0,
          average_stock_level: businessData.average_stock_level ? parseFloat(businessData.average_stock_level) : null,
        },
        integrations: {
          monthly_visitors: integrationData.monthly_visitors ? parseInt(integrationData.monthly_visitors) : null,
          monthly_page_views: integrationData.monthly_page_views ? parseInt(integrationData.monthly_page_views) : null,
        },
        website_analysis: websiteAnalysis,
      };

      const response = await axios.post(`${API_URL}/valuations`, payload, { timeout: 60000 });
      setValuationResult(response.data);
      setCurrentStep(5);
      
      // Navigate to unique results URL for tracking
      if (location.pathname !== '/ValuationEcom') {
        navigate('/ValuationEcom', { state: { valuationResult: response.data }, replace: true });
      }
      
      // Push event to Google Tag Manager dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'valuation_form_submitted',
        'valuation_id': response.data.id,
        'valuation_low': response.data.profit_valuation?.low,
        'valuation_high': response.data.profit_valuation?.high,
        'valuation_average': response.data.profit_valuation?.average,
        'business_type': businessData.business_type,
        'user_email': leadData.email,
        'user_name': leadData.name
      });
      
      // Track Google Ads conversion
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18354459269/VGg5CKSO-OMcEIWly7BE',
          'value': 1.0,
          'currency': 'AUD'
        });
      }
      
      // Track valuation completed event
      if (window.trackValuationCompleted) {
        const valuationBand = response.data.valuation_average < 250000 ? "under_250k" :
                             response.data.valuation_average < 500000 ? "250k_500k" :
                             response.data.valuation_average < 1000000 ? "500k_1m" : "over_1m";
        window.trackValuationCompleted({
          sessionId: response.data.id,
          businessCategory: businessData.business_type,
          saleTimeframe: leadData.sell_timeline,
          valuationBand: valuationBand
        });
      }
      
      toast.success("Valuation calculated successfully!");
    } catch (error) {
      console.error("Valuation error:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate valuation");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        {STEPS.map((step) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isCompleted
                    ? "bg-gradient-to-r from-blue-600 to-green-500 text-white"
                    : isActive
                    ? "bg-gradient-to-r from-blue-600 to-green-500 text-white ring-4 ring-blue-100"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <span
                className={`mt-2 text-xs font-medium hidden sm:block ${
                  isActive || isCompleted ? "text-slate-900" : "text-slate-400"
                }`}
              >
                {step.title}
              </span>
            </div>
          );
        })}
      </div>
      <Progress value={progress} className="h-2" />
    </div>
  );

  // Step 1: Website Analysis
  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in" data-testid="step-1-website-analysis">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900">Analyse Your Website</h2>
        <p className="text-slate-600 mt-2">Enter your website URL to get an instant AI-powered evaluation</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="website_url" className="text-slate-700 font-medium">Website URL *</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              id="website_url"
              placeholder="www.yourbusiness.com.au"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="h-14 text-lg pl-11"
              data-testid="input-website-url"
            />
          </div>
        </div>

        <Button
          onClick={handleWebsiteAnalysis}
          disabled={isAnalyzing || !websiteUrl}
          className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
          data-testid="btn-analyze"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analysing Website...
            </>
          ) : (
            <>
              <Search className="w-5 h-5 mr-2" />
              Analyse Website
            </>
          )}
        </Button>
        
        <Button
          variant="outline"
          onClick={() => {
            if (websiteUrl) {
              setLeadData(prev => ({ ...prev, website_url: websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}` }));
            }
            setCurrentStep(2);
          }}
          className="w-full h-12 text-slate-600 hover:text-slate-900 border-slate-300"
          data-testid="btn-skip-analysis"
        >
          Skip Analysis & Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {websiteAnalysis && (
        <Card className="border-emerald-200 bg-emerald-50 mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />
              Website Analysis Complete
            </CardTitle>
            <CardDescription className="text-emerald-700">
              {websiteAnalysis.summary}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs mb-1">Overall Score</p>
                <p className="text-2xl font-bold text-emerald-600">{websiteAnalysis.overall_score}<span className="text-sm text-slate-400">/100</span></p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs mb-1">Speed</p>
                <p className="text-xl font-semibold text-slate-900">{websiteAnalysis.speed_score}/10</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs mb-1">Usability</p>
                <p className="text-xl font-semibold text-slate-900">{websiteAnalysis.usability_score}/10</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-slate-500 text-xs mb-1">Design</p>
                <p className="text-xl font-semibold text-slate-900">{websiteAnalysis.design_score}/10</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="bg-white rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">Business Type</p>
                <p className="font-semibold text-slate-900">{websiteAnalysis.business_type || "Detected"}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">Revenue Model</p>
                <p className="font-semibold text-slate-900">{websiteAnalysis.revenue_model || "Unknown"}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">Traffic Potential</p>
                <p className="font-semibold text-slate-900">{websiteAnalysis.traffic_potential_score}/10</p>
              </div>
            </div>
            {websiteAnalysis.product_service && (
              <div className="bg-white rounded-lg p-3 mt-3">
                <p className="text-slate-500 text-xs mb-1">Product/Service Detected</p>
                <p className="font-medium text-slate-700 text-sm">{websiteAnalysis.product_service}</p>
              </div>
            )}
            
            <Button
              onClick={() => setCurrentStep(2)}
              className="w-full mt-4 h-12 bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-continue-to-details"
            >
              Continue to Valuation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="bg-slate-50 rounded-xl p-4 mt-4">
        <h3 className="font-medium text-slate-900 mb-2">What we'll analyse:</h3>
        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-500" />
            Website Speed
          </div>
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-emerald-500" />
            Design Quality
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-500" />
            User Experience
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            Traffic Potential
          </div>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-emerald-500" />
            Revenue Model
          </div>
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-500" />
            Business Type
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Lead Capture with additional questions
  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in" data-testid="step-2-lead-capture">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900">Your Details</h2>
        <p className="text-slate-600 mt-2">Enter your contact details to receive your valuation</p>
      </div>

      {websiteAnalysis && (
        <Card className="border-slate-200 bg-slate-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Analysing</p>
                <p className="font-medium text-slate-900">{leadData.website_url}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Website Score</p>
                <p className="text-2xl font-bold text-emerald-600">{websiteAnalysis.overall_score}/100</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
          <Input
            id="name"
            placeholder="John Smith"
            value={leadData.name}
            onChange={(e) => handleLeadChange("name", e.target.value)}
            className="h-12 text-base"
            data-testid="input-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            value={leadData.email}
            onChange={(e) => handleLeadChange("email", e.target.value)}
            className="h-12 text-base"
            data-testid="input-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+61 (40) 000 0000"
            value={leadData.phone}
            onChange={(e) => handleLeadChange("phone", e.target.value)}
            className="h-12 text-base"
            data-testid="input-phone"
          />
        </div>

        <div className="border-t border-slate-200 pt-5 mt-2">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">When are you looking to sell? *</Label>
            <Select
              value={leadData.sell_timeline}
              onValueChange={(v) => handleLeadChange("sell_timeline", v)}
            >
              <SelectTrigger className="h-12" data-testid="select-sell-timeline">
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                {SELL_TIMELINE.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">How would you prefer to manage your sale? *</Label>
          <Select
            value={leadData.sale_preference}
            onValueChange={(v) => handleLeadChange("sale_preference", v)}
          >
            <SelectTrigger className="h-12" data-testid="select-sale-preference">
              <SelectValue placeholder="Select preference" />
            </SelectTrigger>
            <SelectContent>
              {SALE_PREFERENCE.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  // Step 3: Business Details with additional fields
  const renderStep3 = () => {
    const isEcommerce = businessData.business_type === "ecommerce";
    
    return (
      <div className="space-y-6 animate-fade-in" data-testid="step-3-business-details">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900">Business Details</h2>
          <p className="text-slate-600 mt-2">Tell us about your business to calculate an accurate valuation</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Business Type *</Label>
            <Select
              value={businessData.business_type}
              onValueChange={(v) => handleBusinessChange("business_type", v)}
            >
              <SelectTrigger className="h-12" data-testid="select-business-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Years Operating *</Label>
            <Input
              type="number"
              placeholder="e.g., 3"
              value={businessData.years_operating}
              onChange={(e) => handleBusinessChange("years_operating", e.target.value)}
              className="h-12"
              data-testid="input-years-operating"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Average Monthly Revenue ($)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="number"
                placeholder="e.g., 50000"
                value={businessData.monthly_revenue}
                onChange={(e) => handleBusinessChange("monthly_revenue", e.target.value)}
                className="h-12 pl-9"
                data-testid="input-monthly-revenue"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Monthly Profit ($)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="number"
                placeholder="e.g., 15000"
                value={businessData.monthly_profit}
                onChange={(e) => handleBusinessChange("monthly_profit", e.target.value)}
                className="h-12 pl-9"
                data-testid="input-monthly-profit"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              This is the amount returned to you each month before you pay yourself - i.e., how much do you earn from the platform each month.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Annual Revenue Growth (%) *</Label>
            <div className="relative">
              <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="number"
                placeholder="e.g., 25"
                value={businessData.revenue_growth_rate}
                onChange={(e) => handleBusinessChange("revenue_growth_rate", e.target.value)}
                className="h-12 pl-9"
                data-testid="input-growth-rate"
              />
            </div>
            <p className="text-xs text-slate-500">Year-over-year revenue growth percentage</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Owner Hours/Week *</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="number"
                placeholder="e.g., 20"
                value={businessData.owner_hours_per_week}
                onChange={(e) => handleBusinessChange("owner_hours_per_week", e.target.value)}
                className="h-12 pl-9"
                data-testid="input-owner-hours"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Recurring Revenue (%)</Label>
            <Input
              type="number"
              placeholder="e.g., 60"
              value={businessData.recurring_revenue_percentage}
              onChange={(e) => handleBusinessChange("recurring_revenue_percentage", e.target.value)}
              className="h-12"
              data-testid="input-recurring-revenue"
            />
            <p className="text-xs text-slate-500">Percentage of revenue from subscriptions or repeat customers</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Churn Rate (%/month)</Label>
            <Input
              type="number"
              placeholder="e.g., 3"
              value={businessData.churn_rate}
              onChange={(e) => handleBusinessChange("churn_rate", e.target.value)}
              className="h-12"
              data-testid="input-churn-rate"
            />
            <p className="text-xs text-slate-500">Monthly customer/subscriber churn rate</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Traffic Diversity *</Label>
            <Select
              value={businessData.traffic_diversity}
              onValueChange={(v) => handleBusinessChange("traffic_diversity", v)}
            >
              <SelectTrigger className="h-12" data-testid="select-traffic-diversity">
                <SelectValue placeholder="Select diversity" />
              </SelectTrigger>
              <SelectContent>
                {TRAFFIC_DIVERSITY.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Revenue Type *</Label>
            <Select
              value={businessData.revenue_type}
              onValueChange={(v) => handleBusinessChange("revenue_type", v)}
            >
              <SelectTrigger className="h-12" data-testid="select-revenue-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {REVENUE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stock Level - Only for Ecommerce */}
          {isEcommerce && (
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-700 font-medium">Average Stock Level ($)</Label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="e.g., 25000"
                  value={businessData.average_stock_level}
                  onChange={(e) => handleBusinessChange("average_stock_level", e.target.value)}
                  className="h-12 pl-9"
                  data-testid="input-stock-level"
                />
              </div>
              <p className="text-xs text-slate-500">Average value of inventory held at any time</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Step 4: Traffic Data
  const renderStep4 = () => (
    <div className="space-y-6 animate-fade-in" data-testid="step-4-traffic">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900">Traffic Data</h2>
        <p className="text-slate-600 mt-2">Help us understand your website traffic (optional)</p>
      </div>
      
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Monthly Traffic
          </CardTitle>
          <CardDescription>
            Enter your average monthly traffic metrics from Google Analytics or similar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-slate-700">Monthly Visitors</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  name="monthly_visitors"
                  type="number"
                  placeholder="e.g., 50000"
                  value={integrationData.monthly_visitors}
                  onChange={handleIntegrationChange}
                  className="h-12 pl-9"
                  data-testid="input-monthly-visitors"
                />
              </div>
              <p className="text-xs text-slate-500">Unique visitors per month</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Monthly Page Views</Label>
              <div className="relative">
                <BarChart3 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  name="monthly_page_views"
                  type="number"
                  placeholder="e.g., 150000"
                  value={integrationData.monthly_page_views}
                  onChange={handleIntegrationChange}
                  className="h-12 pl-9"
                  data-testid="input-monthly-pageviews"
                />
              </div>
              <p className="text-xs text-slate-500">Total page views per month</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
        <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-800">Why traffic matters</p>
          <p className="text-xs text-emerald-700 mt-1">
            Higher traffic levels often indicate a more established business and can positively influence your valuation. 
            Skip this section if you don't have this data readily available.
          </p>
        </div>
      </div>
    </div>
  );

  // Helper to get quality tier color
  const getQualityTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case "premium": return "text-emerald-600 bg-emerald-100";
      case "strong": return "text-blue-600 bg-blue-100";
      case "good": return "text-slate-600 bg-slate-100";
      case "moderate": return "text-amber-600 bg-amber-100";
      case "high risk": return "text-red-600 bg-red-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  // Step 5: Results
  const renderStep5 = () => {
    if (!valuationResult) return null;

    const bqs = valuationResult.business_quality_score;
    const useRevenueValuation = valuationResult.valuation_method_used === "revenue";
    const displayWebsiteAnalysis = websiteAnalysis || valuationResult.website_analysis;

    return (
      <div className="space-y-6 animate-fade-in" data-testid="step-5-results">
        {/* Website Analysis Score */}
        {displayWebsiteAnalysis && (
          <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="w-5 h-5 text-emerald-600" />
                Website Evaluation
              </CardTitle>
              {displayWebsiteAnalysis.summary && (
                <CardDescription>{displayWebsiteAnalysis.summary}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Overall</p>
                  <p className="text-2xl font-bold text-emerald-600">{displayWebsiteAnalysis.overall_score}<span className="text-xs text-slate-400">/100</span></p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Speed</p>
                  <p className="text-lg font-semibold">{displayWebsiteAnalysis.speed_score}/10</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Usability</p>
                  <p className="text-lg font-semibold">{displayWebsiteAnalysis.usability_score}/10</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Design</p>
                  <p className="text-lg font-semibold">{displayWebsiteAnalysis.design_score}/10</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Traffic</p>
                  <p className="text-lg font-semibold">{displayWebsiteAnalysis.traffic_potential_score}/10</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Business Quality Score */}
        {bqs && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="w-5 h-5 text-emerald-600" />
                Business Quality Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-4xl font-bold text-slate-900">{bqs.score}/100</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getQualityTierColor(bqs.quality_tier)}`}>
                    {bqs.quality_tier}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Valuation Adjustment</p>
                  <p className={`text-lg font-semibold ${bqs.valuation_adjustment >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {bqs.valuation_adjustment >= 0 ? "+" : ""}{bqs.valuation_adjustment}%
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {bqs.strengths?.length > 0 && (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-emerald-800 mb-2">Strengths</p>
                    <ul className="space-y-1">
                      {bqs.strengths.slice(0, 5).map((s, i) => (
                        <li key={i} className="text-xs text-emerald-700 flex items-start gap-1">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {bqs.risks?.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800 mb-2">Risks</p>
                    <ul className="space-y-1">
                      {bqs.risks.slice(0, 5).map((r, i) => (
                        <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dual Valuation Display */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Profit Valuation */}
          <Card className={`border-2 ${!useRevenueValuation ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calculator className="w-4 h-4" />
                Profit-Based Valuation
                {!useRevenueValuation && (
                  <span className="ml-auto text-xs bg-emerald-600 text-white px-2 py-0.5 rounded">Selected</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(valuationResult.profit_valuation?.average || valuationResult.valuation_average)}</p>
              <p className="text-xs text-amber-700 font-medium mt-1">+ Stock at Value (SAV)</p>
              <p className="text-sm text-slate-500 mt-1">
                {valuationResult.adjusted_multiple}× monthly profit
              </p>
              <div className="flex gap-4 mt-3 text-sm">
                <div>
                  <p className="text-slate-400 text-xs">Low</p>
                  <p className="font-medium">{formatCurrency(valuationResult.profit_valuation?.low || valuationResult.valuation_low)}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">High</p>
                  <p className="font-medium">{formatCurrency(valuationResult.profit_valuation?.high || valuationResult.valuation_high)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Valuation */}
          <Card className={`border-2 ${useRevenueValuation ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="w-4 h-4" />
                Revenue-Based Valuation
                {useRevenueValuation && (
                  <span className="ml-auto text-xs bg-emerald-600 text-white px-2 py-0.5 rounded">Selected</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(valuationResult.revenue_valuation?.value || 0)}</p>
              <p className="text-xs text-amber-700 font-medium mt-1">+ Stock at Value (SAV)</p>
              <p className="text-sm text-slate-500 mt-1">
                {valuationResult.revenue_valuation?.multiple || 0}× annual revenue
              </p>
              {valuationResult.revenue_valuation?.value > 0 && (
                <p className="text-xs text-slate-400 mt-2">
                  Annual Revenue: {formatCurrency((businessData.monthly_revenue || 0) * 12)}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Valuation Method Explanation */}
        {useRevenueValuation && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800">Revenue-Based Valuation Used</p>
                  <p className="text-sm text-blue-700 mt-1">
                    This business shows strong recurring revenue and growth characteristics. 
                    Because of this, investors often value companies like this on a revenue multiple rather than profit.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Exit Recommendation */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-4 h-4 text-emerald-600" />
              Recommended Exit Strategy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{valuationResult.exit_recommendation}</p>
          </CardContent>
        </Card>

        {/* AI Commentary */}
        {valuationResult.ai_commentary && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                AI Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-wrap">
                {valuationResult.ai_commentary}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="text-center pt-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 inline-flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <p className="text-emerald-800 text-sm font-medium">
              A detailed valuation report has been emailed to {leadData.email}
            </p>
          </div>
          <div>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="h-12 px-8"
              data-testid="btn-start-new"
            >
              Start New Valuation
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="valuation-widget">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex-shrink-0">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png"
              alt="Ecommerce Brokers"
              className="h-10 w-auto"
            />
          </a>
          <a
            href="/"
            className="text-slate-600 hover:text-blue-600 text-sm font-medium"
          >
            ← Back to Home
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-green-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            Ecommerce Valuation Guide
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2">
            What's Your Ecommerce Business Worth?
          </h1>
          <p className="text-slate-600 text-lg">
            Get an instant AI-powered valuation with website analysis
          </p>
        </div>

        {/* Main Card */}
        <Card className="border-0 shadow-widget bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6 md:p-10">
            {currentStep < 5 && renderStepIndicator()}

            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}

            {/* Navigation Buttons */}
            {currentStep > 1 && currentStep < 5 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="h-12 px-6"
                  data-testid="btn-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>

                {currentStep < 4 ? (
                  <Button
                    onClick={handleNext}
                    className="h-12 px-8 bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
                    data-testid="btn-next"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="h-12 px-8 bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
                    data-testid="btn-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        Get My Valuation
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 space-y-4">
          <div className="flex items-center justify-center gap-4">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png"
              alt="Ecommerce Brokers"
              className="h-8 opacity-60"
            />
            <span className="text-slate-400">•</span>
            <img
              src="https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png"
              alt="Bonza"
              className="h-6 opacity-60"
            />
          </div>
          <p className="text-slate-400 text-sm">
            Ecommerce Brokers — Powered by Bonza Business & Franchise Sales
          </p>
          <div className="flex justify-center gap-4 text-xs text-slate-400">
            <a href="/privacy" className="hover:text-slate-600">Privacy Policy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-slate-600">Terms</a>
            <span>•</span>
            <a href="/disclaimer" className="hover:text-slate-600">Valuation Disclaimer</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValuationWidget;
